"use client";

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface SecurityFeature {
  id: string;
  icon: string;
  title: string;
  description: string;
}

const SecuritySection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const securityFeatures: SecurityFeature[] = [
  {
    id: 'security_encryption',
    icon: 'LockClosedIcon',
    title: '256-bit Encryption',
    description: 'Military-grade encryption protects your messages at rest and in transit.'
  },
  {
    id: 'security_pipa',
    icon: 'ShieldCheckIcon',
    title: 'PIPA Compliant',
    description: 'Fully compliant with Bermuda Personal Information Protection Act.'
  },
  {
    id: 'security_mfa',
    icon: 'FingerPrintIcon',
    title: 'Multi-Factor Authentication',
    description: 'Extra security layer ensures only you can access your content.'
  },
  {
    id: 'security_delivery',
    icon: 'CheckBadgeIcon',
    title: 'Verified Delivery',
    description: 'Confirmation system ensures your messages reach their destination.'
  }];


  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="security" ref={sectionRef} className="py-24 bg-background">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-20">
          <span className="inline-block px-4 py-2 bg-accent/10 text-accent rounded-full text-sm font-semibold mb-6 uppercase tracking-wider">
            Your Trust Matters
          </span>
          <h2 className="font-jakarta font-bold text-4xl md:text-5xl text-foreground mb-6 tracking-tight">
            Bank-Level Security
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
            Your most precious memories deserve the highest level of protection. We use industry-leading security measures.
          </p>
        </div>

        {/* Split Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Security Features */}
          <div
            className={`space-y-6 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`
            }>
            
            {securityFeatures.map((feature, index) =>
            <div
              key={feature.id}
              className="flex items-start gap-4 p-6 bg-card rounded-2xl border border-border hover:shadow-lg transition-all duration-300"
              style={{ transitionDelay: `${index * 100}ms` }}>
              
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center shrink-0">
                  <Icon name={feature.icon as any} size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-jakarta font-semibold text-lg text-foreground mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            )}

            {/* Trust Badge */}
            <div className="pt-8 border-t border-border">
              <div className="flex items-center gap-4 text-muted-foreground">
                <Icon name="UserGroupIcon" size={24} className="text-primary" />
                <p className="text-sm font-medium">
                  <span className="text-foreground font-bold">Trusted by 5,000+ families</span> to protect their most precious memories
                </p>
              </div>
            </div>
          </div>

          {/* Right: Testimonial Card */}
          <div
            className={`transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`
            }
            style={{ transitionDelay: '200ms' }}>
            
            <div className="bg-gradient-to-br from-primary to-secondary rounded-3xl p-8 text-white shadow-2xl">
              <Icon name="ChatBubbleLeftRightIcon" size={48} className="text-white/40 mb-6" />
              <blockquote className="text-xl font-crimson italic leading-relaxed mb-6">
                &quot;Knowing my final messages are secure and will reach my children exactly when I want them to gives me incredible peace of mind. Legacy is a gift to my family.&quot;
              </blockquote>
              <div className="flex items-center gap-4">
                <AppImage
                  src="/images/testimonial-old-person-2.png"
                  alt="Portrait of Sarah Mitchell, smiling woman with brown hair in professional attire"
                  className="w-16 h-16 rounded-full object-cover border-4 border-white/20" />
                
                <div>
                  <p className="font-jakarta font-bold text-lg">Sarah Mitchell</p>
                  <p className="text-white/80 text-sm">Mother of 3, California</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default SecuritySection;